import { useEffect, useState } from 'react';
import { User, Flame, Users } from 'lucide-react';
import { Trophy, Target, Award, TrendingUp, Activity } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../request';
import Navigation from '../components/common/Navigation';
import ToastMessage from '../components/ToastMessage';
import HotStepperRequestModal from '../components/HotStepperRequestModal';
import Header from '../components/common/Header';

const GroupDashboard = () => {
  const [group, setGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [hotStepper, setHotStepper] = useState(null);
  const [requestStatus, setRequestStatus] = useState(null);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [hotStepperRequests, setHotStepperRequests] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    const fetchGroupData = async () => {
      try {
        const res = await api.get('/group-dashboard');
        setGroup(res.data.group);
        setMembers(res.data.members);
        setHotStepper(res.data.hot_stepper);
        setRequestStatus(res.data.request_status);
      } catch (error) {
        console.error('Failed to load group data', error);
      }
    };

    fetchGroupData();
  }, []);



const fetchHotStepperRequests = async () => {
  try {
    const res = await api.get('hotstepper/requests'); // Backend should return requests where hot_stepper_id = currentUser.id
    setHotStepperRequests(res.data);
    if (res.data.length > 0) setShowRequestModal(true);
  } catch (err) {
    console.error('Failed to fetch requests', err);
  }
};

  const canRequestHotStepper = 
    !hotStepper &&
    members.length > 0 &&
    members.every((m) => m.today_steps >= 3000);

  return (
    <div className="min-h-screen bg-background-light">
      <ToastMessage
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ message: '', type: '' })}
      />

      <HotStepperRequestModal
        isOpen={showRequestModal}
        onClose={() => setShowRequestModal(false)}
        />

      {/* Header */}
      <Header />

     

      {/* Main Section */}
      <main className="max-w-7xl mx-auto py-8 px-4 lg:ml-64 space-y-8">
      {/* Group Summary - Enhanced Cards Layout */}
      {group && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Group Name & City Card */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-2xl border border-blue-200 col-span-1 md:col-span-2">
            <div className="flex items-center space-x-3 mb-2">
              <div className="p-2 bg-blue-500 rounded-lg">
                <Users className="w-5 h-5 text-white" />
              </div>
              <h1 className="text-xl font-bold text-gray-900">{group.name}</h1>
            </div>
            <p className="text-gray-600 flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              {group.city} • {group.active_member_count} active members
            </p>
          </div>

          {/* Weekly Steps Card */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 p-6 rounded-2xl border border-green-200">
            <div className="flex items-center justify-between mb-2">
              <div className="p-2 bg-green-500 rounded-lg">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-xs font-medium text-green-700 bg-green-200 px-2 py-1 rounded-full">
                {group.step_tier}
              </span>
            </div>
            <p className="text-2xl font-bold text-gray-900">
                  {(group.total_weekly_steps ?? 0).toLocaleString()}

              </p>
            <p className="text-sm text-gray-600">Weekly Steps</p>
          </div>

          {/* Points & Hot Stepper Card */}
          {/* <div className="bg-gradient-to-br from-yellow-50 to-orange-100 p-6 rounded-2xl border border-yellow-200">
            <div className="flex items-center justify-between mb-4">
              <div className="p-2 bg-yellow-500 rounded-lg">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900">{group.points}</span>
            </div>
            <p className="text-sm text-gray-600 mb-2">Total Points</p>
            <div className="flex items-center text-xs text-gray-500">
              <Award className="w-3 h-3 mr-1" />
              Hot Stepper: {group?.current_hot_stepper?.name ?? 'None'}
            </div>
          </div> */}
        </div>
      )}

      {/* Members Table - Enhanced Design */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-indigo-500 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Group Members</h2>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Member</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Today's Steps</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Weekly Total</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Status</th>
                <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Baton</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {members.map((member, index) => (
                <tr key={member.id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {member.name.charAt(0)}
                      </div>
                      <span className="font-medium text-gray-900">{member.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-lg font-semibold text-gray-900">
                      {(member.today_steps ?? 0).toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-gray-700"> {(member.weekly_steps ?? 0).toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {member.today_steps >= 3000 ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-1.5"></span>
                        Eligible
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <span className="w-1.5 h-1.5 bg-red-400 rounded-full mr-1.5"></span>
                        Not Eligible
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {member.is_baton_holder && (
                      <div className="inline-flex items-center justify-center w-8 h-8 bg-yellow-100 rounded-full">
                        <span className="text-lg">🏃</span>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Hot Stepper Request - Enhanced Card */}
      {canRequestHotStepper && (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200 overflow-hidden">
          <div className="p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-500 rounded-lg">
                <Target className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Need a Hot Stepper?</h2>
            </div>
            <p className="text-gray-600 mb-6">
              Great news! All members are eligible. You can now request a Hot Stepper to join your group and help boost your collective performance.
            </p>
            <button
              className="bg-secondary text-white px-6 py-3 rounded-xl font-medium  transition-all duration-200 transform hover:scale-105 shadow-lg"
              onClick={() => setShowRequestModal(true)}
            >
              Request Hot Stepper
            </button>
          </div>
        </div>
      )}

      {!canRequestHotStepper && !hotStepper && (
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-amber-500 rounded-lg">
              <Target className="w-5 h-5 text-white" />
            </div>
            <p className="text-amber-800 font-medium">
              You need all members to have 3,000+ steps today to request a Hot Stepper.
            </p>
          </div>
        </div>
      )}

      {requestStatus && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl p-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-500 rounded-lg">
              <Activity className="w-5 h-5 text-white" />
            </div>
            <p className="text-blue-800 font-medium">{requestStatus}</p>
          </div>
        </div>
      )}
    </main>


       {/* Sidebar */}
      <nav className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-white shadow-sm z-20 flex-col">
        <Navigation />
      </nav>
      <div className="md:hidden block sticky bottom-0 z-50 bg-white border-t border-gray-200 p-2">
        <Navigation orientation='horizontal' />
      </div>
    </div>
  );
};

export default GroupDashboard;
